import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Pressable, TextInput } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedJobAtom,
    selectedVersionAtom,
    selectedVersionLockedAtom,
    jobUserDataAtom,
    userNameAtom,
    userIdAtom,
} from '../store/company';
import { updateUserDataArray, deleteUserData } from '../db/company';
import moment from 'moment';
import XLSX from 'xlsx';

const TAB_NAME = 'Project Notes';

const COLORS = {
    navy: '#173659',
    navyDark: '#0F2A4A',
    accent: '#2563eb',
    warningAmber: '#d97706',
};

const typeOptions = [
    { label: 'Changes from Bid', value: 'Changes from Bid' },
    { label: 'Change Order Related', value: 'Change Order Related' },
    { label: 'Lessons Learned', value: 'Lessons Learned' },
    { label: 'Risk Log', value: 'Risk Log' },
    { label: 'PSR Notes - Margin Change', value: 'PSR Notes - Margin Change' },
    { label: 'PSR Notes - Forecast Change', value: 'PSR Notes - Forecast Change' },
];

const statusOptions = [
    { label: 'Ongoing', value: 'Ongoing' },
    { label: 'Complete', value: 'Complete' },
];

const ProjectNotes = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [userName] = useAtom(userNameAtom);
    const [userId] = useAtom(userIdAtom);

    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});
    const [editingCards, setEditingCards] = useState({});
    const [filterType, setFilterType] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');
    const lastLoadKeyRef = useRef(null);
    const fileInputRef = useRef();

    const generateId = () => Math.floor(10000000 + Math.random() * 90000000).toString();

    // Extract unique CO numbers from jobUserData
    const coNumbers = React.useMemo(() => {
        if (!jobUserData || !selectedJob) return [];
        const cos = new Set();
        jobUserData.forEach(d => {
            if (d?.Forecast_Tab === 'Change Order' &&
                (d.BU_ID || d.id || '').toString() === selectedJob.toString()) {
                const coNum = d.Row_Label || '';
                if (coNum) cos.add(coNum);
            }
        });
        return Array.from(cos).sort();
    }, [jobUserData, selectedJob]);

    const createBlankNote = () => ({
        _localId: generateId(),
        title: '',
        type: 'Lessons Learned',
        status: 'Ongoing',
        description: '',
        linkedCOs: [],
        date: moment().format('MM/DD/YYYY'),
        author: userName || '',
        _isNew: true,
        _dirty: false,
    });

    useEffect(() => {
        lastLoadKeyRef.current = null;
        setNotes([]);
        setIsLoading(true);
    }, [selectedJob, selectedVersion]);

    useEffect(() => {
        if (!jobUserData || !selectedJob || !selectedVersion) return;
        const loadKey = `${selectedJob}|${selectedVersion}`;
        if (lastLoadKeyRef.current === loadKey) return;
        lastLoadKeyRef.current = loadKey;

        setIsLoading(true);
        try {
            const noteRows = jobUserData.filter(d =>
                (d.Forecast_Tab || d.tab) === TAB_NAME &&
                (d.BU_ID || d.id || '').toString() === selectedJob.toString()
            );

            const grouped = {};
            noteRows.forEach(row => {
                const noteId = row.Row_Label || row.rowLabel || '';
                if (!noteId) return;
                if (!grouped[noteId]) grouped[noteId] = [];
                grouped[noteId].push(row);
            });

            const loaded = Object.keys(grouped).map(noteId => {
                const rows = grouped[noteId];
                const header = rows[0];
                const vendorStr = header.Vendor_Name || header.vendor || '';
                let linkedCOs = [];
                if (vendorStr) {
                    try { linkedCOs = vendorStr.split(',').filter(Boolean); } catch { linkedCOs = []; }
                }
                return {
                    _localId: generateId(),
                    _dbGuid: header.Forecast_User_Data_GUID || header.Guid_ID || header.guidId || null,
                    _dbRowId: header.Forecast_Row_ID || header.rowId || null,
                    title: header.Forecast_Value || header.value || '',
                    type: header.PO_Number || header.po || 'Lessons Learned',
                    status: header.Status || header.status || 'Ongoing',
                    description: header.Description || header.description || '',
                    linkedCOs,
                    date: header.Project_Start || header.projectStart || '',
                    author: header.Resource_Code || header.resourceCode || header.User_Name || '',
                    _isNew: false,
                    _dirty: false,
                };
            });

            loaded.sort((a, b) => {
                const da = moment(a.date, 'MM/DD/YYYY');
                const db = moment(b.date, 'MM/DD/YYYY');
                if (!da.isValid()) return 1;
                if (!db.isValid()) return -1;
                return db.valueOf() - da.valueOf();
            });

            setNotes(loaded);
        } catch (err) {
            console.error('Error loading project notes:', err);
        }
        setIsLoading(false);
    }, [jobUserData, selectedJob, selectedVersion]);

    const addNote = () => {
        const blank = createBlankNote();
        setNotes(prev => [blank, ...prev]);
        setExpandedCards(prev => ({ ...prev, [blank._localId]: true }));
        setEditingCards(prev => ({ ...prev, [blank._localId]: true }));
    };

    const updateNote = (localId, field, value) => {
        setNotes(prev => prev.map(n =>
            n._localId === localId ? { ...n, [field]: value, _dirty: true } : n
        ));
    };

    const toggleCO = (localId, coNum) => {
        setNotes(prev => prev.map(n => {
            if (n._localId !== localId) return n;
            const has = n.linkedCOs.includes(coNum);
            const linkedCOs = has
                ? n.linkedCOs.filter(c => c !== coNum)
                : [...n.linkedCOs, coNum];
            return { ...n, linkedCOs, _dirty: true };
        }));
    };

    const toggleCard = (localId) => {
        setExpandedCards(prev => ({ ...prev, [localId]: !prev[localId] }));
    };

    const saveNote = async (note) => {
        if (!note.title) {
            alert('Title is required');
            return;
        }

        setIsSaving(true);
        try {
            const noteId = note.title.replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 40);
            const isUpdate = !note._isNew && note._dbGuid;
            const rowId = isUpdate ? note._dbRowId : `note_${noteId}_${generateId()}`;

            const row = {
                userId: userId || '154045',
                userName: userName || '',
                tab: TAB_NAME,
                rowId: rowId,
                version: selectedVersion + '',
                id: selectedJob,
                lockedVersion: lockedVersion,
                fieldName: 'noteEntry',
                value: note.title,
                description: note.description || '',
                status: note.status || 'Ongoing',
                po: note.type || '',
                resourceCode: note.author || userName || '',
                projectStart: note.date || moment().format('MM/DD/YYYY'),
                vendor: (note.linkedCOs || []).join(',').substring(0, 50),
                rowLabel: noteId,
                ...(isUpdate ? { updateRow: 1, guidId: note._dbGuid + '' } : { insertRow: 1 }),
            };

            if (!note._isNew && !note._dbGuid && note._dbRowId) {
                try {
                    await deleteUserData({
                        id: selectedJob,
                        forecastGuid: selectedVersion.toString(),
                        rowId: note._dbRowId,
                    });
                } catch (err) {
                    console.warn('Could not delete old note row:', err);
                }
            }

            await updateUserDataArray([row]);

            setJobUserData(prev => {
                if (!prev) return prev;
                const filtered = prev.filter(r => {
                    const t = r.Forecast_Tab || r.tab || '';
                    const rid = r.Forecast_Row_ID || r.rowId || '';
                    const bu = (r.BU_ID || r.id || '').toString();
                    if (t !== TAB_NAME) return true;
                    if (bu !== selectedJob.toString()) return true;
                    if (isUpdate && rid === note._dbRowId) return false;
                    if (!note._isNew && !note._dbGuid && rid === note._dbRowId) return false;
                    return true;
                });
                return [...filtered, {
                    Forecast_Tab: TAB_NAME,
                    Forecast_Row_ID: rowId,
                    Forecast_Field_Name: 'noteEntry',
                    Forecast_Value: note.title,
                    Description: note.description || '',
                    Status: note.status || 'Ongoing',
                    PO_Number: note.type || '',
                    Resource_Code: note.author || userName || '',
                    Project_Start: note.date || '',
                    Vendor_Name: (note.linkedCOs || []).join(',').substring(0, 50),
                    Row_Label: noteId,
                    BU_ID: selectedJob,
                    Forecast_Guid: selectedVersion + '',
                }];
            });

            setNotes(prev => prev.map(n =>
                n._localId === note._localId
                    ? { ...n, _isNew: false, _dirty: false, _dbRowId: rowId }
                    : n
            ));

            setEditingCards(prev => { const next = { ...prev }; delete next[note._localId]; return next; });

            lastLoadKeyRef.current = null;

        } catch (err) {
            console.error('Error saving note:', err);
            alert('Failed to save note. Please try again.');
        }
        setIsSaving(false);
    };

    const removeNote = async (note) => {
        if (note._dbGuid || note._dbRowId) {
            try {
                await deleteUserData({
                    id: selectedJob,
                    forecastGuid: selectedVersion,
                    lockedVersion: lockedVersion,
                    rowId: note._dbGuid ? note._dbGuid + '' : note._dbRowId + '',
                    tab: TAB_NAME,
                });

                setJobUserData(prev => {
                    if (!prev) return prev;
                    return prev.filter(r => {
                        const t = r.Forecast_Tab || r.tab || '';
                        const rid = r.Forecast_Row_ID || r.rowId || '';
                        if (t !== TAB_NAME) return true;
                        return rid !== note._dbRowId;
                    });
                });
            } catch (err) {
                console.error('Error deleting note:', err);
            }
        }

        setNotes(prev => prev.filter(n => n._localId !== note._localId));
        setShowDeleteConfirm(null);
    };

    const handleExport = () => {
        const data = getFilteredNotes().map(n => ({
            'Title': n.title,
            'Type': n.type,
            'Status': n.status,
            'Description': n.description,
            'Linked COs': (n.linkedCOs || []).join(', '),
            'Date': n.date,
            'Author': n.author,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Project Notes');
        XLSX.writeFile(wb, `ProjectNotes_${selectedJob}_${moment().format('YYYYMMDD')}.xlsx`);
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const wb = XLSX.read(evt.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(ws);
            const imported = jsonData.map(row => ({
                _localId: generateId(),
                title: row['Title'] || '',
                type: row['Type'] || 'Lessons Learned',
                status: row['Status'] || 'Ongoing',
                description: row['Description'] || '',
                linkedCOs: (row['Linked COs'] || '').split(',').map(s => s.trim()).filter(Boolean),
                date: row['Date'] || moment().format('MM/DD/YYYY'),
                author: row['Author'] || userName || '',
                _isNew: true,
                _dirty: true,
            }));
            setNotes(prev => [...imported, ...prev]);
            imported.forEach(n => setExpandedCards(prev => ({ ...prev, [n._localId]: true })));
        };
        reader.readAsArrayBuffer(file);
        e.target.value = '';
    };

    const getFilteredNotes = () => {
        return notes.filter(n => {
            if (filterType !== 'All' && n.type !== filterType) return false;
            if (filterStatus !== 'All' && n.status !== filterStatus) return false;
            return true;
        });
    };

    const selectStyle = {
        border: '1px solid #d1d5db',
        borderRadius: 6,
        padding: '6px 10px',
        fontSize: 12,
        backgroundColor: '#fff',
        outline: 'none',
    };

    const inputStyle = {
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 6,
        padding: '6px 10px',
        fontSize: 12,
        backgroundColor: '#fff',
        outlineColor: COLORS.accent,
    };

    const labelStyle = {
        fontSize: 10,
        fontWeight: '600',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 3,
    };

    const btnBase = {
        borderRadius: 6,
        paddingHorizontal: 18,
        paddingVertical: 9,
        cursor: 'pointer',
    };

    const statusBadge = (status) => {
        const map = {
            'Ongoing': { bg: '#fef3c7', border: '#fbbf24', text: '#92400e' },
            'Complete': { bg: '#d1fae5', border: '#34d399', text: '#065f46' },
        };
        return map[status] || { bg: '#e2e8f0', border: '#94a3b8', text: '#475569' };
    };

    const typeBadge = (type) => {
        const map = {
            'Changes from Bid': { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
            'Change Order Related': { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
            'Lessons Learned': { bg: '#fef9c3', border: '#fde047', text: '#854d0e' },
            'Risk Log': { bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d' },
            'PSR Notes - Margin Change': { bg: '#e0e7ff', border: '#a5b4fc', text: '#3730a3' },
            'PSR Notes - Forecast Change': { bg: '#f0fdf4', border: '#86efac', text: '#166534' },
        };
        return map[type] || { bg: '#f1f5f9', border: '#cbd5e1', text: '#334155' };
    };

    const filteredNotes = getFilteredNotes();

    const typeCounts = {};
    typeOptions.forEach(t => { typeCounts[t.value] = 0; });
    notes.forEach(n => { if (typeCounts[n.type] !== undefined) typeCounts[n.type]++; });

    if (isLoading) {
        return (
            <View style={{ padding: 20, alignItems: 'center' }}>
                <Text style={{ fontSize: 13, color: '#64748b' }}>Loading notes...</Text>
            </View>
        );
    }

    return (

        <View>
            {/* Header */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: COLORS.navyDark }}>Project Notes</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Pressable onPress={handleExport} style={{ padding: '3px 8px', cursor: 'pointer', borderRadius: 4, backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                        <Text style={{ fontSize: 10, fontWeight: '600', color: '#64748b' }}>Export</Text>
                    </Pressable>
                    <Pressable onPress={() => fileInputRef.current?.click()} style={{ padding: '3px 8px', cursor: 'pointer', borderRadius: 4, backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                        <Text style={{ fontSize: 10, fontWeight: '600', color: '#64748b' }}>Import</Text>
                    </Pressable>
                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".xlsx,.xls,.csv" onChange={handleImport} />
                    <Pressable onPress={addNote} style={{ padding: '3px 10px', cursor: 'pointer', borderRadius: 4, backgroundColor: COLORS.accent }}>
                        <Text style={{ fontSize: 10, fontWeight: '700', color: '#fff' }}>+ Add Note</Text>
                    </Pressable>
                </View>
            </View>

            {/* Notes list */}
            {filteredNotes.length === 0 && (
                <View style={{ padding: 20, alignItems: 'center' }}>
                    <Text style={{ fontSize: 12, color: '#94a3b8' }}>No notes yet. Click "+ Add Note" to get started.</Text>
                </View>
            )}

            {filteredNotes.map((note) => {
                const tBadge = typeBadge(note.type);
                const isEditing = editingCards[note._localId] || note._isNew || note._dirty;

                /* ─── READ-ONLY CARD ─── */
                if (!isEditing) {
                    return (
                        <View key={note._localId} style={{ backgroundColor: '#fff', borderRadius: 5, marginBottom: 2, borderWidth: 1, borderColor: '#e5e7eb', overflow: 'hidden', minHeight: 0 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, padding: '4px 8px' }}>
                                <View style={{ backgroundColor: tBadge.bg, borderRadius: 2, padding: '0px 5px', minWidth: 0 }}>
                                    <Text style={{ fontSize: 9, fontWeight: '700', color: tBadge.text }}>{note.type.slice(0, 16)}</Text>
                                </View>
                                <Text style={{ flex: 1, fontSize: 11, fontWeight: '600', color: '#1e293b', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{note.title || '(Untitled)'}</Text>
                                {note.description ? <Text style={{ fontSize: 10, color: '#64748b', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{note.description}</Text> : null}
                                {(note.linkedCOs || []).length > 0 && (
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                        {note.linkedCOs.map(co => (
                                            <View key={co} style={{ backgroundColor: '#eff6ff', borderRadius: 2, padding: '0px 4px', marginLeft: 1 }}>
                                                <Text style={{ fontSize: 8, fontWeight: '600', color: '#2563eb' }}>{co}</Text>
                                            </View>
                                        ))}
                                    </View>
                                )}
                                <Text style={{ fontSize: 9, color: '#94a3b8', marginLeft: 4 }}>{note.date}</Text>
                                <Text style={{ fontSize: 9, color: '#64748b', fontWeight: '500', marginLeft: 4 }}>{note.author}</Text>
                                <Pressable onPress={() => setEditingCards(prev => ({ ...prev, [note._localId]: true }))}
                                    style={{ padding: '2px 7px', cursor: 'pointer', borderRadius: 3, border: '1px solid #e2e8f0', marginLeft: 4 }}>
                                    <Text style={{ fontSize: 9, fontWeight: '600', color: '#64748b' }}>Edit</Text>
                                </Pressable>
                                {showDeleteConfirm === note._localId ? (
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                        <Pressable onPress={() => removeNote(note)} style={{ padding: '2px 7px', backgroundColor: '#dc2626', borderRadius: 3, cursor: 'pointer' }}>
                                            <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff' }}>Confirm</Text>
                                        </Pressable>
                                        <Pressable onPress={() => setShowDeleteConfirm(null)} style={{ padding: '2px 6px', cursor: 'pointer' }}>
                                            <Text style={{ fontSize: 9, color: '#94a3b8' }}>Cancel</Text>
                                        </Pressable>
                                    </View>
                                ) : (
                                    <Pressable onPress={() => setShowDeleteConfirm(note._localId)} style={{ padding: '2px 6px', cursor: 'pointer', borderRadius: 3, border: '1px solid #fee2e2', marginLeft: 2 }}>
                                        <Text style={{ fontSize: 9, fontWeight: '600', color: '#ef4444' }}>Delete</Text>
                                    </Pressable>
                                )}
                            </View>
                        </View>
                    );
                }

                /* ─── EDIT CARD ─── */
                return (
                    <View key={note._localId} style={{ backgroundColor: '#fefce8', borderRadius: 8, marginBottom: 6, borderWidth: 1, borderColor: '#fbbf24', padding: 14 }}>
                        {/* Title + Type */}
                        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                            <TextInput
                                style={{ ...inputStyle, flex: 1, fontWeight: '600', fontSize: 13, padding: '6px 10px' }}
                                value={note.title}
                                onChangeText={(v) => updateNote(note._localId, 'title', v)}
                                placeholder="Note title..."
                            />
                            <select style={{ ...selectStyle, fontSize: 11, padding: '5px 8px', width: 190 }} value={note.type} onChange={(e) => updateNote(note._localId, 'type', e.target.value)}>
                                {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </View>

                        {/* Date + Author */}
                        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                            <input type="date" style={{ ...inputStyle, fontSize: 11, padding: '4px 8px', width: 140 }}
                                value={note.date ? moment(note.date, 'MM/DD/YYYY').format('YYYY-MM-DD') : ''}
                                onChange={(e) => { const d = moment(e.target.value, 'YYYY-MM-DD'); updateNote(note._localId, 'date', d.isValid() ? d.format('MM/DD/YYYY') : ''); }}
                            />
                            <TextInput style={{ ...inputStyle, fontSize: 11, padding: '5px 8px', width: 160 }} value={note.author} onChangeText={(v) => updateNote(note._localId, 'author', v)} placeholder="Author..." />
                            {/* Linked COs */}
                            {coNumbers.length > 0 && (
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, flex: 1 }}>
                                    <Text style={{ fontSize: 9, color: '#94a3b8', fontWeight: '600' }}>COs:</Text>
                                    {coNumbers.map(co => {
                                        const sel = (note.linkedCOs || []).includes(co);
                                        return (
                                            <Pressable key={co} onPress={() => toggleCO(note._localId, co)}
                                                style={{ backgroundColor: sel ? '#dbeafe' : '#f8fafc', borderWidth: 1, borderColor: sel ? '#3b82f6' : '#e2e8f0', borderRadius: 3, padding: '1px 6px', cursor: 'pointer' }}>
                                                <Text style={{ fontSize: 10, fontWeight: sel ? '700' : '500', color: sel ? '#1e40af' : '#94a3b8' }}>{co}</Text>
                                            </Pressable>
                                        );
                                    })}
                                </View>
                            )}
                        </View>

                        {/* Description */}
                        <textarea
                            style={{ ...inputStyle, width: '100%', minHeight: 70, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5, fontSize: 11 }}
                            value={note.description}
                            onChange={(e) => updateNote(note._localId, 'description', e.target.value)}
                            placeholder="Description..."
                        />

                        {/* Action row */}
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 8 }}>
                            {note._dirty && <Text style={{ fontSize: 9, color: COLORS.warningAmber }}>● Unsaved changes</Text>}
                            {!note._isNew && (
                                <Pressable onPress={() => {
                                    if (note._dirty) { updateNote(note._localId, '_dirty', false); }
                                    setEditingCards(prev => { const next = { ...prev }; delete next[note._localId]; return next; });
                                }}
                                    style={{ padding: '5px 14px', cursor: 'pointer', borderRadius: 4, border: '1px solid #d1d5db', backgroundColor: '#fff' }}>
                                    <Text style={{ fontSize: 11, fontWeight: '600', color: '#64748b' }}>Cancel</Text>
                                </Pressable>
                            )}
                            <Pressable onPress={() => saveNote(note)} disabled={isSaving}
                                style={{ padding: '5px 18px', cursor: isSaving ? 'default' : 'pointer', borderRadius: 4, backgroundColor: isSaving ? '#94a3b8' : COLORS.accent }}>
                                <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff' }}>{isSaving ? 'Saving...' : 'Save'}</Text>
                            </Pressable>
                        </View>
                    </View>
                );
            })}
        </View>
    );
};

export default ProjectNotes;
